import { useState } from 'react';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import ResizableTextarea from '@/shared-components/inputs/components/ResizableTextarea';
import SettingsHeading from '@/shared-components/text/components/SettingsHeading';
import Utils from '@/utils';

import ConfirmUsernameChange from './components/ConfirmUsernameChange';
import ProfileCoverImage from './components/ProfileCoverImage';
import ProfileImage from './components/ProfileImage';
import ProfileUsername from './components/ProfileUsername';
import { normalizeCoverImage } from './galleryCoverImage.utils';

import './index.css';

export default function EditProfile() {
  const { state, stateHandler } = useStateHandler();
  const t = useTranslations('common.user_settings.edit_profile');
  const [formState, setFormState] = useState({
    user_image: state.user_session.user_image,
    gallery_cover_image: state.user_session.gallery_cover_image,
    artist_cover_image: state.user_session.artist_cover_image,
    user_name: state.user_session.user_name,
    user_username: state.user_session.user_username,
    user_profile_info: {
      user_description: state.user_session.user_profile_info.user_description,
      user_location: state.user_session.user_profile_info.user_location
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [openConfirmUsernameChange, setOpenConfirmUsernameChange] = useState(false);
  const [savingProfileImage, setSavingProfileImage] = useState(false);
  const [savingCoverImage, setSavingCoverImage] = useState(false);
  const isCoverProfile = ['gallery', 'artist'].includes(state.user_session.user_type);
  const coverField = state.user_session.user_type === 'artist'
    ? 'artist_cover_image'
    : 'gallery_cover_image';

  function updateFormState(key, value) {
    setFormState(prevValue => Utils.addAttributeToObject(key, value, prevValue));
  }

  function getBaseProfilePayload() {
    const defaultProfileInfo = state.user_session.user_profile_info || {};
    return {
      user_image: state.user_session.user_image,
      gallery_cover_image: state.user_session.gallery_cover_image,
      artist_cover_image: state.user_session.artist_cover_image,
      user_name: state.user_session.user_name,
      user_username: state.user_session.user_username,
      user_profile_info: {
        user_description: defaultProfileInfo.user_description,
        user_location: defaultProfileInfo.user_location
      }
    };
  }

  async function autoSaveProfileImage(nextUserImage) {
    if (typeof nextUserImage === "undefined") {
      return;
    }

    const payload = {
      ...getBaseProfilePayload(),
      user_image: nextUserImage
    };

    setSavingProfileImage(true);

    try {
      const { response, data } = await UserService.editProfile(payload);

      if (response.ok) {
        stateHandler.set("user_session", data);
        setFormState(prevValue => ({
          ...prevValue,
          user_image: data.user_image
        }));
      } else if (response.status === 400) {
        setError({ element: data.error_data.element, error_code: data.error_type });
        alert(t('alerts.profile_picture_update_failed'));
        setFormState(prevValue => ({
          ...prevValue,
          user_image: state.user_session.user_image
        }));
      } else {
        alert(t('alerts.generic_error_contact', { email: 'contact@suarte.art' }));
        setFormState(prevValue => ({
          ...prevValue,
          user_image: state.user_session.user_image
        }));
      }
    } finally {
      setSavingProfileImage(false);
    }
  }

  async function autoSaveCoverImage(nextCoverImage) {
    if (typeof nextCoverImage === "undefined" || !isCoverProfile) {
      return;
    }

    const normalizedCoverImage = normalizeCoverImage(nextCoverImage);
    if (typeof normalizedCoverImage === "undefined") {
      return;
    }

    const payload = {
      [coverField]: normalizedCoverImage
    };

    setSavingCoverImage(true);

    try {
      const { response, data } = await UserService.editProfile(payload);

      if (response.ok) {
        stateHandler.set("user_session", data);
        setFormState(prevValue => ({
          ...prevValue,
          [coverField]: data[coverField] ?? normalizedCoverImage
        }));
      } else if (response.status === 400) {
        setError({ element: data.error_data.element, error_code: data.error_type });
        alert(t('alerts.profile_picture_update_failed'));
        setFormState(prevValue => ({
          ...prevValue,
          [coverField]: state.user_session[coverField]
        }));
      } else {
        alert(t('alerts.generic_error_contact', { email: 'contact@suarte.art' }));
        setFormState(prevValue => ({
          ...prevValue,
          [coverField]: state.user_session[coverField]
        }));
      }
    } finally {
      setSavingCoverImage(false);
    }
  }

  async function submitEditProfileLogic() {
    setLoading(true);
    setError(null);

    if(formState.user_image?.loading || formState.gallery_cover_image?.loading || formState.artist_cover_image?.loading) {
      setLoading(false);
      alert(t('alerts.wait_image_upload'));
    } else {
      const { response, data } = await UserService.editProfile(formState);

      if(response.ok) {
        setLoading(false);
        setSuccess(true);
        stateHandler.set("user_session", data);
        setTimeout(()=>setSuccess(false), 3000);

      } else if(response.status === 400) {
        setLoading(false);
        setError({ element: data.error_data.element, error_code: data.error_type });
      }
    } 
  }

  function submitEditProfile(e) {
    e.preventDefault();

    if(formState.user_username && formState.user_username !== state.user_session.user_username) {
      setOpenConfirmUsernameChange(true);
    } else {
      submitEditProfileLogic();
    }
  }

  return (
    <>
      <SettingsHeading text={t('heading')}/>

      <form onSubmit={submitEditProfile} className="edit-profile-form">
        <div className={`edit-profile-form__media-row${isCoverProfile ? " gallery" : ""}`}>
          <ProfileImage
            userType={state.user_session.user_type}
            formState={formState}
            setFormState={setFormState}
            onImageChange={autoSaveProfileImage}
            disableImageActions={savingProfileImage || savingCoverImage}
          />

          {isCoverProfile && (
            <ProfileCoverImage
              userId={state.user_session._id}
              userType={state.user_session.user_type}
              coverField={coverField}
              formState={formState}
              setFormState={setFormState}
              onCoverChange={autoSaveCoverImage}
              disableCoverActions={savingProfileImage || savingCoverImage}
            />
          )}
        </div>

        {state.user_session.user_type !== "collector" && (
          <PublicFormInput 
            element="user_name" 
            placeholder={
              state.user_session.user_type === 'gallery'
                ? t('form.placeholders.gallery_name')
                : t('form.placeholders.artist_name')
            }
            readOnly={loading || success} 
            error={error} 
            value={formState.user_name} 
            onChange={e=>updateFormState("user_name", e.target.value)}
            margin
          />
        )}

        <ProfileUsername
          userType={state.user_session.user_type}  
          userFlags={state.user_session.user_flags}
          readOnly={loading || success} 
          error={error} 
          value={formState.user_username} 
          onChange={e=>updateFormState("user_username", e.target.value)}
        />

        <ResizableTextarea 
          element="user_profile_info.user_description" 
          placeholder={t('form.placeholders.biography')}
          readOnly={loading || success} 
          error={error} 
          value={formState.user_profile_info.user_description} 
          onChange={e=>updateFormState("user_profile_info.user_description", e.target.value)}
          margin
        />

        <PublicFormInput 
          element="user_profile_info.user_location" 
          placeholder={t('form.placeholders.location')}
          readOnly={loading || success} 
          error={error} 
          value={formState.user_profile_info.user_location} 
          onChange={e=>updateFormState("user_profile_info.user_location", e.target.value)}
          margin
        />

        <ContinueButton 
          className="edit-profile-form__submit-button font-size-medium" 
          successClassName="edit-profile-form__submit-button" 
          loading={loading} 
          success={success} 
          successText={t('form.buttons.saved')}
        >
          {t('form.buttons.save_changes')}
        </ContinueButton>
      </form>

      <ConfirmUsernameChange
        open={openConfirmUsernameChange}
        close={()=>setOpenConfirmUsernameChange(false)}
        onConfirm={()=>submitEditProfileLogic()}
        newUsername={formState.user_username}
        userType={state.user_session.user_type}
      />
    </>
  );
}
